// PKGV.A3 (#794) — label-search autocomplete over the panel's currently
// visible (type/edge-filtered) nodes. a11y pattern mirrors
// TimezoneDropdown.tsx (L8, locked): role="combobox" input + role="listbox"
// dropdown of role="option" items, arrow-key navigation, Enter to select,
// Escape to close.
//
// Search is a "find and jump to" tool, not an additional visibility
// filter (see Sprint-A3-recon.md / impl-prompt): it doesn't hide
// non-matching nodes from the canvas, it surfaces up to 10 ranked
// suggestions and pans/centers on whichever one is selected (V3: via
// PKGCanvas's existing centerNodeId, no re-fetch, no highlight styling —
// L3 defers the border/scale highlight to a fast-follow).

import { useState, useRef, useEffect, useMemo } from 'react';
import type { PKGNode } from '@blueprint/shared';
import { rankLabelMatches, nodeLabel } from './pkgFilter.ts';

export interface PKGLabelSearchComboboxProps {
  nodes: PKGNode[];
  value: string;
  onChange: (query: string) => void;
  onSelect: (node: PKGNode) => void;
}

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '6px 10px',
  fontFamily: 'var(--font-body, inherit)', fontSize: 13, color: 'var(--text)',
  background: 'var(--surface)', border: '1px solid var(--border)',
  borderRadius: 'var(--radius-sm, 4px)', outline: 'none', boxSizing: 'border-box',
};

const dropdownStyle: React.CSSProperties = {
  position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 20,
  maxHeight: 240, overflowY: 'auto',
  background: 'var(--surface)', border: '1px solid var(--border)',
  borderRadius: 'var(--radius-sm, 4px)', boxShadow: 'var(--shadow-md, 0 4px 12px rgba(0,0,0,0.15))',
  marginTop: 2,
};

export function PKGLabelSearchCombobox({ nodes, value, onChange, onSelect }: PKGLabelSearchComboboxProps) {
  const [open, setOpen] = useState(false);
  const [highlightIdx, setHighlightIdx] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const results = useMemo(() => rankLabelMatches(nodes, value), [nodes, value]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (highlightIdx >= 0 && listRef.current) {
      const el = listRef.current.querySelector(`[data-pkg-search-idx="${highlightIdx}"]`);
      el?.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightIdx]);

  const select = (node: PKGNode) => {
    onSelect(node);
    setOpen(false);
    setHighlightIdx(-1);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!open) {
      if (e.key === 'ArrowDown') { setOpen(true); e.preventDefault(); }
      return;
    }
    if (e.key === 'Escape') {
      setOpen(false);
      e.preventDefault();
    } else if (e.key === 'ArrowDown') {
      setHighlightIdx(i => Math.min(i + 1, results.length - 1));
      e.preventDefault();
    } else if (e.key === 'ArrowUp') {
      setHighlightIdx(i => Math.max(i - 1, 0));
      e.preventDefault();
    } else if (e.key === 'Enter' && highlightIdx >= 0 && highlightIdx < results.length) {
      select(results[highlightIdx]);
      e.preventDefault();
    }
  };

  return (
    <div ref={containerRef} style={{ position: 'relative', marginBottom: 8 }}>
      <input
        style={inputStyle}
        value={value}
        onChange={e => { onChange(e.target.value); setOpen(true); setHighlightIdx(-1); }}
        onFocus={() => { if (value) setOpen(true); }}
        onKeyDown={handleKeyDown}
        placeholder="Search nodes…"
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label="Search nodes by label"
        role="combobox"
        data-testid="pkg-search-input"
      />
      {open && results.length > 0 && (
        <div ref={listRef} style={dropdownStyle} role="listbox">
          {results.map((node, idx) => (
            <div
              key={node.id}
              data-pkg-search-idx={idx}
              role="option"
              aria-selected={idx === highlightIdx}
              onMouseDown={e => { e.preventDefault(); select(node); }}
              onMouseEnter={() => setHighlightIdx(idx)}
              style={{
                padding: '6px 12px', fontSize: 13, cursor: 'pointer',
                color: 'var(--text)',
                background: idx === highlightIdx ? 'var(--hover, rgba(0,0,0,0.04))' : 'transparent',
              }}
            >
              {nodeLabel(node)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
